import { uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useReportError } from '@/hooks/useReportError';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { getNodes } from '@/utils/graphqlUtils';

import {
  HouseholdChat_ConversationListItemFragment,
  useHouseholdChatConversationsQuery,
} from '../graphql/HouseholdChat.generated';
import { buildHouseholdConversationFilters } from './householdConversationFilters';

const PAGE_SIZE = 20;

interface UseHouseholdChatConversationsParams {
  householdId: string;
}

interface UseHouseholdChatConversationsResult {
  conversations: HouseholdChat_ConversationListItemFragment[];
  hasMore: boolean;
  loadMore: () => Promise<void>;
  loading: boolean;
  loadingMore: boolean;
  refetch: () => void;
}

/**
 * The household conversations the current user may see, most recently active
 * first, with cursor pagination. Mirrors the Notifications context pattern:
 * manual page state + loadMore via fetchMore.
 *
 * See buildHouseholdConversationFilters for which conversations that is.
 */
export function useHouseholdChatConversations({
  householdId,
}: UseHouseholdChatConversationsParams): UseHouseholdChatConversationsResult {
  const currentUser = useCurrentUser();
  const canEditHousehold = useCanEditHouseholdData();
  const { reportError } = useReportError();

  const [conversations, setConversations] = useState<
    HouseholdChat_ConversationListItemFragment[]
  >([]);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const endCursorRef = useRef<string | null>(null);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    endCursorRef.current = endCursor;
  }, [endCursor]);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  const conversationFilters = useMemo(
    () =>
      buildHouseholdConversationFilters({
        canEditHousehold,
        // currentUser is guaranteed present by `skip`; fall back to '' for types.
        currentUserId: currentUser?.id ?? '',
      }),
    [canEditHousehold, currentUser?.id]
  );

  const { fetchMore, loading, refetch } = useHouseholdChatConversationsQuery({
    skip: !currentUser,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      conversationFilters,
      first: PAGE_SIZE,
      householdId,
    },
    onCompleted: (data) => {
      setConversations(getNodes(data.conversations));
      setHasMore(Boolean(data.conversations.pageInfo.hasNextPage));
      setEndCursor(data.conversations.pageInfo.endCursor ?? null);
    },
    onError: (error) => {
      reportError('Failed to load household chat conversations', error);
    },
  });

  const loadMore = useCallback(async () => {
    if (!hasMoreRef.current || loadingMoreRef.current) {
      return;
    }

    setLoadingMore(true);
    try {
      const { data } = await fetchMore({
        // Ensure the Time scalar (updatedAt) is parsed on paged results.
        context: { forceParseScalars: true },
        variables: { after: endCursorRef.current },
      });

      // updated_at is a mutable sort key: a conversation bumped between page
      // fetches can be returned again by the next page, so dedupe on append.
      setConversations((previous) =>
        uniqBy([...previous, ...getNodes(data.conversations)], (c) => c.id)
      );
      setHasMore(Boolean(data.conversations.pageInfo.hasNextPage));
      setEndCursor(data.conversations.pageInfo.endCursor ?? null);
    } catch (error) {
      reportError(
        'Failed to load more household chat conversations',
        error instanceof Error ? error : undefined
      );
    } finally {
      setLoadingMore(false);
    }
  }, [fetchMore, reportError]);

  return {
    conversations,
    hasMore,
    loadMore,
    loading,
    loadingMore,
    refetch: () => {
      void refetch();
    },
  };
}
